import type { EventIdPublicOrSlug } from "@shared/schemas/branded-id.schemas";
import type { EventPublicView } from "@shared/schemas/event.schemas";
import type { EventViewUnion } from "@shared/schemas/event-views.schemas";
import { dal } from "@frontend/shared/data/frontend-dal";
import type { FrontendDAL } from "@frontend/shared/data/frontend-dal";
import { useGatedAction } from "../gated-action";
import type {
  ConnectionRequestActions,
  ConnectionRequestIdPublic,
} from "./use-connection-request-actions";
import { useConnectionRequestActions as useConnectionRequestActionsImpl } from "./use-connection-request-actions";
import type { EventOwnerActions } from "./use-event-owner-actions";
import { useEventOwnerActions as useEventOwnerActionsImpl } from "./use-event-owner-actions";
import type {
  EventParticipantActions,
  ForParticipantScope,
} from "./use-event-participant-actions";
import { useEventParticipantActions as useEventParticipantActionsImpl } from "./use-event-participant-actions";

type DirectoryHooks = FrontendDAL["directory"];

/**
 * Any event view a frontend slot can hold. Wider than the backend's
 * `EventViewUnion` by `EventPublicView`, because the anonymous read
 * (`useAnonymousActions().useGetEventPublicView`) fills the same `event` slots.
 */
export type EventView = EventViewUnion | EventPublicView;

export interface EventActionsInput {
  readonly eventIdPublicOrSlug: EventIdPublicOrSlug;
}

export interface EventActionsExtra {
  readonly refetch: () => void;
  readonly useGetDirectoryEntries: DirectoryHooks["useGetEntries"];
  readonly useGetDirectoryEntriesBatch: DirectoryHooks["useGetDirectoryEntriesBatch"];
  readonly useGetProfileDetail: DirectoryHooks["useGetProfileDetail"];
  readonly usePrefetchAllDirectoryPages: DirectoryHooks["usePrefetchAllPages"];
  readonly useCreateDirectoryExport: DirectoryHooks["useCreateExport"];
  readonly useForOwner: () => EventOwnerActions;
  readonly useForParticipant: (
    scope: ForParticipantScope,
  ) => EventParticipantActions;
  readonly useForConnectionRequest: (
    requestIdPublic: ConnectionRequestIdPublic,
  ) => ConnectionRequestActions;
}

export interface EventActions extends EventActionsExtra {
  readonly isLoading: boolean;
  readonly error: Error | null;
  readonly event: EventView | undefined;
}

const useGetDirectoryEntries: DirectoryHooks["useGetEntries"] = (options) =>
  dal().directory.useGetEntries(options);

const useGetDirectoryEntriesBatch: DirectoryHooks["useGetDirectoryEntriesBatch"] =
  (options) => dal().directory.useGetDirectoryEntriesBatch(options);

const useGetProfileDetail: DirectoryHooks["useGetProfileDetail"] = (options) =>
  dal().directory.useGetProfileDetail(options);

const usePrefetchAllDirectoryPages: DirectoryHooks["usePrefetchAllPages"] = (
  options,
) => dal().directory.usePrefetchAllPages(options);

const useCreateDirectoryExport: DirectoryHooks["useCreateExport"] = () =>
  dal().directory.useCreateExport();

/**
 * Event actions — the event-scoped node of the actions tree, reached via
 * `useUserActions().useEventActions(...)` (mirrors backend `userActions.forEvent`,
 * which requires authentication). Gated on event access: `subject` (surfaced as
 * `event`) is the authenticated caller's event view, or `null` while the fetch is
 * pending, the caller is unauthenticated, or they are forbidden (403) — in which
 * case `error` carries the failure. The resolved `event.$roles` is what the nested
 * owner/participant/connection-request gates read. Always non-null so those nested
 * hooks are callable unconditionally (Rules of Hooks); it has no mutations of its
 * own (no bundle). Public (unauthenticated) event access lives on
 * `useAnonymousActions().useGetEventPublicView`, not here.
 */
export function useEventActions(input: EventActionsInput): EventActions {
  const { eventIdPublicOrSlug } = input;
  const {
    isLoading,
    error,
    subject,
    bundle: _bundle,
    ...extra
  } = useGatedAction({
    useDeps: () => {
      const currentUser = dal().auth.useCurrentUser();
      const isAuthenticated =
        !currentUser.isLoading && currentUser.data != null;
      const eventQuery = dal().events.useGetEvent({
        eventIdPublicOrSlug,
        enabled: isAuthenticated,
      });
      return { currentUser, eventQuery };
    },
    isLoading: (d) => d.currentUser.isLoading || d.eventQuery.isLoading,
    error: (d) =>
      d.eventQuery.error instanceof Error ? d.eventQuery.error : null,
    authorize: (d) => d.eventQuery.data ?? null,
    extra: (d, resolved): EventActionsExtra => {
      const context = {
        event: resolved.subject ?? undefined,
        isLoading: resolved.isLoading,
        error: resolved.error,
      };
      return {
        refetch: () => void d.eventQuery.refetch(),
        useGetDirectoryEntries,
        useGetDirectoryEntriesBatch,
        useGetProfileDetail,
        usePrefetchAllDirectoryPages,
        useCreateDirectoryExport,
        useForOwner: () => useEventOwnerActionsImpl(context),
        useForParticipant: (scope) =>
          useEventParticipantActionsImpl(context, scope),
        useForConnectionRequest: (requestIdPublic) =>
          useConnectionRequestActionsImpl(context, requestIdPublic),
      };
    },
  });

  return { isLoading, error, event: subject ?? undefined, ...extra };
}
